import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getFilmsByIds } from "@/lib/film";
import { featuredIds } from "@/mocks/films";

export default function HeroCarousel() {
  const slides = getFilmsByIds(featuredIds);
  const [active, setActive] = useState(0);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setActive((current) => (current + 1) % slides.length);
    }, 6500);
    return () => window.clearInterval(timer);
  }, [slides.length]);

  const goTo = (index: number) => setActive((index + slides.length) % slides.length);
  const current = slides[active];

  if (!current) return null;

  return (
    <section className="relative w-full h-[520px] md:h-[600px] overflow-hidden bg-foreground-950">
      {slides.map((film, index) => (
        <div
          key={film.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === active ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
          aria-hidden={index !== active}
        >
          <img
            src={film.backdrop ?? film.cover}
            alt={`${film.title} 影片剧照`}
            title={`${film.title} 日本电影`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/90 via-foreground-950/60 to-foreground-950/20"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-transparent to-foreground-950/30"></div>
        </div>
      ))}

      <div className="relative z-10 h-full max-w-[1280px] mx-auto px-4 md:px-6 flex items-center">
        <div className="w-full max-w-[620px] animate-fade-up" key={current.id}>
          <div className="flex items-center gap-3 mb-4">
            <span className="px-2 py-1 rounded-md bg-primary-600 text-background-50 text-[11px] tracking-wide font-label">
              本期焦点
            </span>
            <h1 className="text-xs md:text-[13px] tracking-[0.2em] text-background-200/90 font-label">
              日本一级片 · 影视资料库
            </h1>
          </div>

          <h2 className="font-heading text-3xl md:text-[46px] leading-[1.15] text-background-50">
            {current.title}
          </h2>
          <p className="mt-2 text-sm text-background-200/80 font-label tracking-wide">{current.originalTitle}</p>

          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs md:text-sm text-background-200/85 font-label">
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line w-3.5 h-3.5 flex items-center justify-center"></i>
              {current.year}
            </span>
            <span className="w-px h-3 bg-background-50/30"></span>
            <span className="flex items-center gap-1">
              <i className="ri-star-fill text-accent-400 w-3.5 h-3.5 flex items-center justify-center"></i>
              {current.rating.toFixed(1)}
            </span>
            <span className="w-px h-3 bg-background-50/30"></span>
            <span>{current.genres.join(" / ")}</span>
            <span className="w-px h-3 bg-background-50/30"></span>
            <span>{current.duration} 分钟</span>
          </div>

          <p className="mt-4 text-sm md:text-[15px] leading-relaxed text-background-100/90 line-clamp-3 max-w-[540px]">
            {current.synopsis}
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${current.id}`}
              className="h-11 px-6 inline-flex items-center gap-2 rounded-md bg-primary-600 text-background-50 text-sm whitespace-nowrap hover:bg-primary-500 transition-colors"
            >
              查看影片资料
              <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
            </Link>
            <Link
              to="/topic"
              className="h-11 px-6 inline-flex items-center rounded-md border border-background-50/40 text-background-50 text-sm whitespace-nowrap hover:bg-background-50/10 transition-colors"
            >
              浏览专题合集
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 md:bottom-8 left-0 right-0 z-10">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            {slides.map((film, index) => (
              <button
                key={film.id}
                type="button"
                onClick={() => goTo(index)}
                aria-label={`查看第 ${index + 1} 部焦点影片：${film.title}`}
                className={`h-1 rounded-full transition-all duration-300 cursor-pointer ${
                  index === active ? "w-10 bg-accent-500" : "w-5 bg-background-50/40 hover:bg-background-50/70"
                }`}
              />
            ))}
          </div>

          <div className="hidden sm:flex items-center gap-2">
            <span className="text-xs text-background-200/70 mr-1 font-label">
              {String(active + 1).padStart(2, "0")} / {String(slides.length).padStart(2, "0")}
            </span>
            <button
              type="button"
              onClick={() => goTo(active - 1)}
              aria-label="上一部焦点影片"
              className="w-9 h-9 flex items-center justify-center rounded-md border border-background-50/30 text-background-50 hover:bg-background-50/10 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-s-line w-4 h-4 flex items-center justify-center"></i>
            </button>
            <button
              type="button"
              onClick={() => goTo(active + 1)}
              aria-label="下一部焦点影片"
              className="w-9 h-9 flex items-center justify-center rounded-md border border-background-50/30 text-background-50 hover:bg-background-50/10 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center"></i>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}